import { OptionTypeEnum, toBigWithDefault } from '@talos/kyoko';
import type { OptionStrategyRow } from '../types';
import { BS, type BSVariables } from './BlackScholes';

export const PAYOFF_SAMPLE_SIZE = 1000;
export const PAYOFF_PLOT_SIZE = 400;
export type Point = [number, number];

export const calculateY = (
  rows: OptionStrategyRow[],
  partialVars: Omit<BSVariables, 'strike'>,
  initial: number,
  direction: 'Buy' | 'Sell',
  legExpiryDiff?: number
) => {
  return rows.reduce((acc, row, index) => {
    const strike = Number(row.strike!.value);
    const bsVariables: BSVariables = {
      ...partialVars,
      strike,
      term: index === 0 && legExpiryDiff ? partialVars.term + legExpiryDiff / 365 : partialVars.term,
    };

    const legPrice = row.type?.value === OptionTypeEnum.Call ? BS.call(bsVariables) : BS.put(bsVariables);
    const ratio = toBigWithDefault(row.ratio, 1);
    const weightedPrice = Math.abs(ratio.mul(legPrice).toNumber());
    if (ratio.gt(0)) {
      return direction === 'Buy' ? acc + weightedPrice : acc - weightedPrice;
    }
    return direction === 'Buy' ? acc - weightedPrice : acc + weightedPrice;
  }, initial);
};

export function getSampleIncrement(start: number, end: number): number {
  const range = end - start;
  if (range < PAYOFF_SAMPLE_SIZE) {
    return 1;
  }
  return Math.floor(range / PAYOFF_SAMPLE_SIZE);
}

// We sample more sparsely around the ends of the datasource since the curve is typically around the center and we want more accuracy there
export function getPlotIndices(sourceSize: number, targetSize: number) {
  const middleStart = Math.floor(sourceSize * 0.33333);
  const middleEnd = Math.floor(sourceSize * 0.66666);
  const middleCount = targetSize / 2;
  const edgeCount = (targetSize - middleCount) / 2;

  // Sample evenly from the three specified ranges
  const middleIndexes = Array.from(
    { length: middleCount },
    (_, i) => Math.floor((i / middleCount) * (middleEnd - middleStart + 1)) + middleStart
  );
  const leftIndexes = Array.from({ length: edgeCount }, (_, i) => Math.floor((i / edgeCount) * middleStart));
  const rightIndexes = Array.from(
    { length: edgeCount },
    (_, i) => Math.floor((i / edgeCount) * (sourceSize - middleEnd)) + middleEnd
  );

  const sampledIndexes = [...leftIndexes, ...middleIndexes, ...rightIndexes].toSorted((a, b) => a - b);

  return sampledIndexes;
}

export function mapSampleDataToPlotData(sampleData: Point[]): Point[] {
  if (sampleData.length < PAYOFF_PLOT_SIZE) {
    return sampleData;
  }
  const sampledIndexes = getPlotIndices(sampleData.length, PAYOFF_PLOT_SIZE);
  return sampledIndexes.map(i => sampleData[i]);
}
